/* @flow */
import Backbone from "backbone";
import safeAccess from "safe-access";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Environment from "nexia_mobile/environment";

import type { HumanReadableString, Url } from "nexia_mobile/api/types";
import type VognitionHomeProfile from "nexia_mobile/vognition_home_profile";

const Event = {
  BACK_BUTTON: "backbutton",
  APP_PAUSE: "pause",
  APP_RESUME: "resume",
  APP_STOP: "stop",
  APP_RESTART: "restart",
  NOTIFICATION_TOKEN: "notification_token",
  VOGNITION_STATE: "vognition",
  BANNER_MESSAGE: "banner_message",
  CONFIRM: "confirm",
  LOCAL_SETTINGS_CHANGED: "local_settings_changed",
};

export type Geofence = {
  id: string,
  name: string,
  latitude: string,
  longitude: string,
  radius: string,
  hrefAcknowledge: Url,
  hrefEnter: Url,
  hrefExit: Url,
};

export type GeofenceCrossing = {
  direction: string,
  name: string,
  date: string,
};

export type NativeHTTPRequestOptions = {
  method: string,
  url: string,
  body?: ?string,
  headers?: ?{ [key: string]: string },
};

/* eslint-disable no-empty-function, no-unused-vars */
class Base {
  // Gets added to the prototype at the bottom of this file
  Event: typeof Event;

  // Gets added by Backbone.Events mixin at the bottom of this file
  trigger: (eventType: string, ...rest: Array<any>) => void;

  bundleIdentifier: string;
  appVersion: string;
  platformName: string;
  manufacturer: string;
  model: string;
  osVersion: string;
  commandLineArgs: string;
  deviceName: string;
  deviceFamily: string;
  deviceUUID: string;
  currentLocale: string;

  // let the app container do stuff (with access to javascript)
  // and invokes the callback when done.
  initScriptInterface(callback: () => void) {
    // In the browser, use the Page Visibility API to trigger app pause and
    // resume. In mobile platforms, we hook into the native code to detect
    // pause/resume instead.
    window.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        Dispatch("APP_PAUSE");
      } else {
        Dispatch("APP_RESUME");
      }
    });

    this.setDeviceUUID("base");
    // /$FlowFixMe
    const currentLocale = () =>
      navigator.languages && navigator.languages.length
        ? navigator.languages[0]
        : navigator.userLanguage ||
          navigator.language ||
          navigator.browserLanguage ||
          "en-US";
    this.setCurrentLocale(currentLocale());
    callback();
  }

  exitApp() {}

  allowScreenDimming(yesno: boolean = true) {}

  // To be called by native host application
  bannerMessage(message: string) {
    let decodedMessage = decodeURIComponent(message);
    this.trigger(this.Event.BANNER_MESSAGE, decodedMessage);
  }

  confirm(message: string, onConfirm: () => void) {
    let decodedMessage = decodeURIComponent(message);
    this.trigger(this.Event.CONFIRM, decodedMessage, onConfirm);
  }

  getStoredValue(key: string): string {
    return window.localStorage.getItem(key);
  }

  setStoredValue(key: string, value: string) {
    window.localStorage.setItem(key, value);
  }

  clearStoredValue(key: string) {
    window.localStorage.removeItem(key);
  }

  clearStoredValues() {
    window.localStorage.clear();
  }

  canSendFeedback(): boolean {
    return false;
  }

  isMobilePlatform(): boolean {
    return false;
  }

  hasAppInstalled(name: string): Promise<boolean> {
    return Promise.resolve(false);
  }

  obtainPushNotificationToken() {}

  resetAppBadge() {}

  hideSoftKeyboard() {}

  isImageSaveSupported(): boolean {
    return false;
  }

  saveImage(
    base64Data: string
  ): Promise<{ hasPermission: boolean, message: HumanReadableString }> {
    return Promise.reject(new Error("Cannot save images on this platform"));
  }

  getAppInfo() {
    return {
      /* eslint-disable camelcase */
      app_identifier: this.bundleIdentifier,
      app_version: this.appVersion,
      platform: this.platformName,
      manufacturer: this.manufacturer,
      model: this.model,
      os_version: this.osVersion,
      device_uuid: this.deviceUUID,
      currentLocale: this.currentLocale,
      /* eslint-enable camelcase */
    };
  }

  getCommandLineArgs(): string {
    return this.commandLineArgs;
  }

  sendGoogleAnalyticsEvent(category: string, action: string, label: string) {}
  sendGoogleAnalyticsDuration(
    category: string,
    action: string,
    label: string,
    value: ?number
  ) {}

  // Geofencing methods
  isGeofencingSupported(): boolean {
    return false;
  }
  isNotificationSoundConfigurable(): boolean {
    return false;
  }
  checkLocationPermissions(): Promise<boolean> {
    return Promise.resolve(false);
  }
  getLocationServicesWarningMessage(): Promise<HumanReadableString> {
    return Promise.resolve(
      "Nexia needs Location Services to be enabled to use geofencing."
    );
  }
  areLocationServicesEnabled(): Promise<boolean> {
    return Promise.resolve(true);
  }
  openAppPermissionSettings() {
    throw new Error("There are no permission settings on the base platform");
  }
  openLocationServiceSettings() {
    throw new Error("Cannot open location service settings on this platform");
  }
  openWifiSettings() {
    throw new Error("Cannot open wifi settings on this platform");
  }
  setGeofences(geofences: Array<Geofence>): Promise<void> {
    return Promise.resolve();
  }
  startGeofenceService(): Promise<void> {
    return Promise.reject(
      new Error("Geofence service cannot be started on this platform")
    );
  }
  stopGeofenceService(): Promise<void> {
    return Promise.reject(
      new Error("Geofence service cannot be stopped on this platform")
    );
  }
  getGeofenceCrossings(): Promise<Array<GeofenceCrossing>> {
    return Promise.resolve([]);
  }
  // end Geofencing methods

  // methods to be called by native host application
  setBundleIdentifier(bundleIdentifier: string) {
    this.bundleIdentifier = bundleIdentifier;
  }
  setAppVersion(appVersion: string) {
    this.appVersion = appVersion;
  }
  setPlatformName(platformName: string) {
    this.platformName = platformName;
  }
  setManufacturer(manufacturer: string) {
    this.manufacturer = manufacturer;
  }
  setModel(model: string) {
    this.model = model;
  }
  setOsVersion(osVersion: string) {
    this.osVersion = osVersion;
  }
  setDeviceName(deviceName: string) {
    this.deviceName = deviceName;
  }
  setUrlEncodedDeviceName(urlEncodedDeviceName: string) {
    let deviceName = decodeURIComponent(urlEncodedDeviceName);
    this.deviceName = deviceName;
  }
  setCommandLineArgs(value: string) {
    this.commandLineArgs = value;
  }
  setDeviceFamily(value: string) {
    this.deviceFamily = value;
  }
  setDeviceUUID(value: string) {
    this.deviceUUID = value;
  }
  setCurrentLocale(value: string) {
    this.currentLocale = value;
  }

  triggerBackButton() {
    this.trigger(this.Event.BACK_BUTTON);
  }
  triggerAppPause() {
    this.trigger(this.Event.APP_PAUSE);
  }
  triggerAppResume() {
    this.trigger(this.Event.APP_RESUME);
  }
  triggerAppStop() {
    this.trigger(this.Event.APP_STOP);
  }
  triggerAppRestart() {
    this.trigger(this.Event.APP_RESTART);
  }
  triggerNotificationTokenObtained(token: string) {
    this.trigger(this.Event.NOTIFICATION_TOKEN, { token });
  }
  triggerLocalSettingsChanged() {
    this.trigger(this.Event.LOCAL_SETTINGS_CHANGED);
  }
  // end methods to be called by native host application

  // Vognition methods
  checkMicrophonePermissions(): Promise<"granted" | "denied" | "undetermined"> {
    return Promise.resolve("undetermined");
  }

  isVognitionSupported(): boolean {
    return false;
  }

  isVoiceAssistantSupported(): boolean {
    return false;
  }

  enableVognition() {}

  // To be called by native host application
  updateVognitionState(encodedStateJSON: string) {
    let state;
    try {
      const stateJSON = decodeURIComponent(encodedStateJSON);
      state = JSON.parse(stateJSON);
    } catch (error) {
      throw new Error("Failed to parse vognition state update");
    }

    Dispatch("VOGNITION_STATE_UPDATE", state);
  }

  runVognitionListener() {
    throw new Error("Vognition is not supported on this platform");
  }

  stopVognitionListener() {
    throw new Error("Vognition is not supported on this platform");
  }

  speakString(message: string) {
    throw new Error("Vognition is not supported on this platform");
  }

  setVognitionHomeProfile(profile: VognitionHomeProfile): Promise<void> {
    return Promise.reject(
      new Error("Vognition is not supported on this platform")
    );
  }
  // end Vognition methods

  // Does the platform allow programmatic access to Wifi networks?
  canGetWifiNetworks(): boolean {
    return false;
  }

  // Does the platform allow you to programmatically connect to a Wifi network?
  canConnectToWifiNetwork(): boolean {
    return false;
  }

  // Start a Wifi network scan. The SSID of each found network will be passed
  // to callback as an array of strings.
  wifiScan(callback: (Array<string>) => void) {}

  // Connect to a WiFi network.
  connectToWifiNetwork(ssid: string, password: string): Promise<void> {
    return Promise.reject(
      new Error("connectToWifiNetwork not implemented on this platform")
    );
  }

  getBiometricString() {
    return "fingerprint";
  }

  isFingerprintAuthAvailable(): Promise<boolean> {
    return Promise.resolve(false);
  }

  authenticateWithFingerprint(): Promise<{
    success: boolean,
    wasIntentionallyCancelled: boolean,
  }> {
    return Promise.resolve({ success: false, wasIntentionallyCancelled: true });
  }

  cancelFingerprintAuthentication(): void {}

  // Native HTTP methods, to bypass CORS
  canUseNativeHTTP(): boolean {
    return false;
  }

  nativeHTTPRequest(
    options: NativeHTTPRequestOptions
  ): Promise<{ body: string, code: number }> {
    return Promise.reject(
      new Error("Native HTTP is unavailable on this platform")
    );
  }
  // end native HTTP methods

  canDo724OpenAuthEncryption(): boolean {
    return false;
  }

  encryptStringFor724OpenAuth(key: string, input: string): Promise<string> {
    return Promise.reject(
      new Error("724 Open Auth Encryption is unavailable on this platform")
    );
  }

  canEnableRemoteDebugging(): boolean {
    return false;
  }

  enableRemoteDebugging() {
    throw new Error(
      "Enabling remote debugging at runtime is not supported on this platform"
    );
  }

  // Detect if a platform-native screen reader is running so that a better
  // experience can be provided to screen reader users.
  canDetectIfScreenReaderIsRunning(): boolean {
    return false;
  }

  // To be called by native host application
  setIsScreenReaderRunning(value: boolean) {
    Dispatch("SCREEN_READER_RUNNING_CHANGED", value);
  }
  // End screen reader methods

  // On some platforms, the web view isn't visible until the JS code tells the
  // native code it's ready (a splash screen is shown instead). Call this when
  // it's safe to show the DOM (all views are rendered, etc). It should be safe
  // to call this multiple times without effect if the web view is already
  // visible.
  showWebView(): void {}

  // If you call showWebView, you should also call hideWebView on pause so that
  // on a subsequent resume, the splash screen "covers" the app while it decides
  // which view to show
  hideWebView(): void {}

  // Session cache methods
  _sessionCache: { [url: Url]: Object };

  getSessionCache() {
    this._ensureLocalSettingsLoaded();
    return this._sessionCache;
  }

  // When the app first loads, we might write multiple things to the cache (session, locations... etc).
  // These items all get serialized into the same cache (same LocalSettings key). In order to avoid
  // unnecessarily writing the data twice when the data is just going to change anyway, we use a
  // debounce function that waits up to a second before writing.
  _eventuallySaveSessionCache = _.debounce(
    (contents: { [url: Url]: Object }) => {
      let value = JSON.stringify(contents);
      this.setStoredValue("session_cache", value);
    },
    1000
  );

  // NOTE: mutates jsonObj argument, adding jsonObj.result.cached = true
  addToSessionCache(
    jsonObj:
      | { result: { id: Url, cached?: boolean } }
      | { result: { _links: { self: { href: Url } }, cached?: boolean } }
  ) {
    this._ensureLocalSettingsLoaded();

    let url =
      safeAccess(jsonObj, "result.id") ||
      safeAccess(jsonObj, "result._links.self.href");
    if (!url && !Environment.isTestRunner()) {
      console.warn(
        "Attempted to add an item to the session cache that had no URL:",
        jsonObj
      );
    }
    if (jsonObj.result != null) {
      jsonObj.result.cached = true;
    }
    this._sessionCache[url] = jsonObj;
    this._eventuallySaveSessionCache(this._sessionCache);
  }

  clearSessionCache() {
    this._ensureLocalSettingsLoaded();
    this._sessionCache = {};
    this.clearStoredValue("session_cache");
  }

  _ensureLocalSettingsLoaded() {
    if (this._sessionCache != null) {
      return;
    }
    try {
      let value = this.getStoredValue("session_cache");
      if (typeof value !== "string") {
        this._sessionCache = {};
        return;
      }
      let json = JSON.parse(value);
      this._sessionCache = json || {};
    } catch (error) {
      this._sessionCache = {};
    }
  }

  createTemporaryFile(blob: Blob, extension: string): Promise<string> {
    return Promise.resolve(URL.createObjectURL(blob));
  }

  removeTemporaryFile(url: string): Promise<void> {
    URL.revokeObjectURL(url);
    return Promise.resolve();
  }
  // End session cache methods
}

_.extend(Base.prototype, Backbone.Events);

_.extend(Base.prototype, { Event });

export default Base;



// WEBPACK FOOTER //
// ./src/platforms/base.js